import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "replacePicker"]
  static values  = {
    ids:        Array,
    cards:      Array,
    compareUrl: String
  }

  connect() {
    this._timer       = null
    this._pendingCard = null
    this._onOutside   = this._handleOutside.bind(this)
    document.addEventListener("click", this._onOutside)
  }

  disconnect() {
    clearTimeout(this._timer)
    document.removeEventListener("click", this._onOutside)
  }

  search() {
    clearTimeout(this._timer)
    const q = this.inputTarget.value.trim()
    if (q.length < 2) { this._closeDropdown(); return }
    this._timer = setTimeout(() => this._fetch(q), 300)
  }

  cancelReplace() {
    this.replacePickerTarget.classList.add("hidden")
    this._pendingCard = null
  }

  // ── private ──────────────────────────────────────────

  async _fetch(q) {
    const res  = await fetch(`/search.json?q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } })
    const data = await res.json()
    this._renderDropdown(data)
  }

  _renderDropdown(results) {
    const currentIds = this.idsValue

    if (!results.length) {
      this.dropdownTarget.innerHTML = `<div class="px-4 py-3 text-sm text-gray-400 italic">No cards found</div>`
      this.dropdownTarget.classList.remove("hidden")
      return
    }

    this.dropdownTarget.innerHTML = results.map(card => {
      const selected = currentIds.includes(card.id)
      const img      = card.image_url
        ? `<img src="${card.image_url}" alt="${card.name}" class="object-contain w-full h-full p-0.5">`
        : `<svg class="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24"><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm0 5v9h16V9H4z"/></svg>`
      return `
        <button class="compare-result w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${selected ? "cursor-default" : "hover:bg-gray-50"}"
                data-id="${card.id}" data-name="${this._esc(card.name)}"
                ${selected ? "disabled" : ""}>
          <div class="w-12 h-8 bg-gray-50 rounded border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            ${img}
          </div>
          <span class="text-sm text-gray-800 truncate flex-1">${card.name}</span>
          ${selected ? `<span class="text-xs text-gray-400 flex-shrink-0">Added</span>` : ""}
        </button>`
    }).join(`<div class="border-t border-gray-50 mx-4"></div>`)

    this.dropdownTarget.classList.remove("hidden")

    this.dropdownTarget.querySelectorAll(".compare-result:not([disabled])").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        this._selectCard(+btn.dataset.id, btn.dataset.name)
      })
    })
  }

  _selectCard(id, name) {
    const ids = this.idsValue
    if (ids.length < 4) {
      this._navigate([...ids, id])
    } else {
      this._showReplacePicker(id, name)
    }
  }

  _showReplacePicker(newId, newName) {
    this._pendingCard = { id: newId, name: newName }
    this._closeDropdown()

    const cards = this.cardsValue
    this.replacePickerTarget.innerHTML = `
      <div class="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <p class="text-sm font-semibold text-gray-800 mb-3">
          Replace which card with <span class="text-blue-600">${newName}</span>?
        </p>
        <div class="flex flex-wrap gap-2">
          ${cards.map(card => {
            const img = card.image_url
              ? `<img src="${card.image_url}" alt="${card.name}" class="w-10 h-6 object-contain flex-shrink-0">`
              : ""
            return `
              <button class="replace-btn flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors text-sm text-gray-700 group"
                      data-replace-id="${card.id}">
                ${img}
                <span class="truncate max-w-[140px]">${card.name}</span>
                <svg class="w-3.5 h-3.5 text-gray-300 group-hover:text-red-400 flex-shrink-0 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>`
          }).join("")}
        </div>
        <button class="mt-3 text-xs text-gray-400 hover:text-gray-600 underline"
                data-action="click->compare-search#cancelReplace">Cancel</button>
      </div>`

    this.replacePickerTarget.classList.remove("hidden")

    this.replacePickerTarget.querySelectorAll(".replace-btn").forEach(btn => {
      btn.addEventListener("click", () => {
        const ids = [...this.idsValue]
        const idx = ids.indexOf(+btn.dataset.replaceId)
        if (idx !== -1) ids[idx] = this._pendingCard.id
        this._navigate(ids)
      })
    })
  }

  _navigate(ids) {
    const url = new URL(this.compareUrlValue)
    ids.forEach(id => url.searchParams.append("ids[]", id))
    window.location.href = url.toString()
  }

  _closeDropdown() {
    this.dropdownTarget.classList.add("hidden")
  }

  _handleOutside(e) {
    if (!this.element.contains(e.target)) this._closeDropdown()
  }

  _esc(str) {
    return str.replace(/"/g, "&quot;")
  }
}
